import EntitlementBanner from '@css-xp/base/banner';
import { getDeletedLozenge } from '@css-xp/base/entitlement-utils';
import { EntitlementDetails as Entitlement } from '@css-xp/graphtypes/src/generated-types-new';
import {
  isFreePlanEntitlement,
  isOfferingArchived,
  isOfferingAtNotice,
  isOfferingPaid,
} from '@atlassian/cssxp-utils-entitlement';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { differenceInDays, parseISO } from 'date-fns';
import React from 'react';
import { isTrialEntitlement } from './entitlement-summary/entitlment-summary-utils';

type textsType = {
  TITLE?: string;
  MESSAGE: string;
  LINK?: {
    label: string;
    href: string;
  };
};

const getNoOfDays = (expiryDate: string): string => {
  const noOfDays = differenceInDays(new Date(expiryDate), parseISO(new Date().toISOString()));
  if (noOfDays === 1) return `${noOfDays} day`;
  return `${noOfDays} days`;
};

const LINK: textsType['LINK'] = {
  label: 'Learn more about archived offerings.',
  href: 'https://www.atlassian.com', // TODO: update this link
};

const AT_NOTICE = {
  getTitle: (expiryDate: string) =>
    `This offering is in the process of being archived and will be scheduled for cancellation in ${getNoOfDays(
      expiryDate
    )}.`,
  MESSAGE: 'The subscription can be managed until its end date.',
  LINK,
};

const ARCHIVED: textsType = {
  TITLE: 'This offering has been archived and is now expired.',
  MESSAGE: 'However, the subscription can still be moved to an active offering.',
  LINK,
};

const DELETED_STRINGS = {
  MESSAGE: 'This offering has been archived by the vendor and can no longer be managed.',
};
const FREE_STRINGS = {
  AT_NOTICE: {
    ...AT_NOTICE,
    getTitle: (expiryDate: string) =>
      `This offering is in the process of being archived and will expire in ${getNoOfDays(expiryDate)}.`,
  },
  ARCHIVED: {
    ...ARCHIVED,
    TITLE: '',
    MESSAGE: 'This offering has been archived by the vendor and can no longer be managed.',
    LINK: undefined,
  },
};
const TRIAL_STRINGS = {
  AT_NOTICE: {
    ...AT_NOTICE,
    MESSAGE: 'The subscription can be managed until its end date, but the trial can’t be extended.',
  },
  ARCHIVED,
};
const PAID_STRINGS = {
  AT_NOTICE,
  ARCHIVED,
};

const EntitlementPageBanner = function Banner({ entitlement }: { entitlement?: Entitlement }) {
  if (!entitlement) return null;

  const { isApp, offering } = entitlement;
  if (!isApp) return null;

  const isAppArchived = isOfferingArchived(entitlement);
  const isAppAtNotice = isOfferingAtNotice(entitlement);

  if (!(isAppArchived || isAppAtNotice)) return null;

  const appearance = 'warning';
  const isAppDeleted = getDeletedLozenge(entitlement.billingStatus, entitlement.provisioningStatus);
  const isAppFree = isFreePlanEntitlement(entitlement);
  const isAppInTrial = !isAppFree && isTrialEntitlement(entitlement, isStatuspage(entitlement.productId));
  const isAppPaid = !isAppInTrial && isOfferingPaid(entitlement);

  if (isAppDeleted) {
    if (isAppArchived) {
      return <EntitlementBanner appearance={appearance} message={DELETED_STRINGS.MESSAGE} />;
    }
    return null;
  }

  let TEXTS;
  if (isAppFree) {
    TEXTS = FREE_STRINGS;
  } else if (isAppInTrial) {
    TEXTS = TRIAL_STRINGS;
  } else if (isAppPaid) {
    TEXTS = PAID_STRINGS;
  }

  if (TEXTS) {
    if (isAppAtNotice) {
      return (
        <EntitlementBanner
          appearance={appearance}
          title={offering?.expiryDate ? TEXTS.AT_NOTICE.getTitle(offering.expiryDate) : ''}
          message={TEXTS.AT_NOTICE.MESSAGE}
          link={TEXTS.AT_NOTICE.LINK}
        />
      );
    }
    if (isAppArchived) {
      return (
        <EntitlementBanner
          appearance={appearance}
          title={TEXTS.ARCHIVED?.TITLE}
          message={TEXTS.ARCHIVED.MESSAGE}
          link={TEXTS.ARCHIVED?.LINK}
        />
      );
    }
    return null;
  }

  return null;
};

export default EntitlementPageBanner;
